import imgAdobe from './assets/AdobeStock_1155900392.jpeg';
import imgLmbServices from './assets/lmb-marketing.png';
import imgLmbBrands from './assets/grid-lmb-brands.png';
import imgLmbCopy from './assets/grid-lmb-copy.png';
import imgBewusstseinstage from './assets/grid-bewusstseinstage.png';
import imgRemaxWheel from './assets/grid-remax-wheel.png';
import imgRemaxLogin from './assets/grid-remax-login.png';
import imgMylingra from './assets/grid-mylingra.png';
import imgRsqHero from './assets/grid-rsq-hero.png';
import imgRsqExpertise from './assets/grid-rsq-expertise.png';
import imgVoca from './assets/desktop_voca.png';

/**
 * @typedef {Object} GridTile
 * @property {string} id
 * @property {string} thumbnail
 * @property {string} projectId
 */

/** Raster image for phase-2 zoom (middle scroll picture — Bewusstseinstage hero) */
export const THIRD_ZOOM_IMAGE = imgBewusstseinstage;

/** Raster image for phase-3 zoom (third scroll picture — VOCA) */
export const SECOND_ZOOM_IMAGE = imgVoca;

/**
 * @param {GridTile} tile
 * @returns {string}
 */
export function getTileZoomSource(tile) {
  return tile.thumbnail;
}

export function getSecondZoomSource() {
  return SECOND_ZOOM_IMAGE;
}

export function getThirdZoomSource() {
  return THIRD_ZOOM_IMAGE;
}

export const TILE_RADIUS = 0;
export const TILE_ASPECT_RATIO = '4 / 3';

/** Desktop grid layout — column count scales up on wider viewports (see getDesktopColumnCount) */
export const GRID_IMAGES_PER_ROW = 10;
export const GRID_DESKTOP_MIN_COLS = 10;
export const GRID_DESKTOP_MAX_COLS = 28;
/** Target tile width tuned for 13" MacBook at 10 columns */
export const GRID_DESKTOP_MAX_TILE_WIDTH_PX = 54;
export const GRID_GAP_PX_MD = 52;
export const GRID_HORIZONTAL_PADDING_PX = 0;
export const GRID_SECTION_WIDTH_RATIO = 0.95;
export const DESKTOP_TEXT_COLUMN_RATIO = 0.44;
export const DESKTOP_COLUMN_GAP_PX = 28;
export const DESKTOP_IMAGE_ASPECT_RATIO = 16 / 9;
/** Middle zoom ("Design that lands") — Bewusstseinstage hero (2880×1620) */
export const MIDDLE_ZOOM_IMAGE_ASPECT_RATIO = 16 / 9;
export const ZOOM_PRIMARY_IMAGE_SCALE = 1.25;

/**
 * @param {number} baseWidth
 * @param {number} baseHeight
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 */
export function getPrimaryZoomImageDimensions(baseWidth, baseHeight, viewportWidth, viewportHeight) {
  const aspectRatio = baseWidth / baseHeight;
  let targetWidth = baseWidth * ZOOM_PRIMARY_IMAGE_SCALE;
  let targetHeight = baseHeight * ZOOM_PRIMARY_IMAGE_SCALE;

  if (targetHeight > viewportHeight) {
    targetHeight = viewportHeight;
    targetWidth = targetHeight * aspectRatio;
  }

  if (targetWidth > viewportWidth) {
    targetWidth = viewportWidth;
    targetHeight = targetWidth / aspectRatio;
  }

  return { targetWidth, targetHeight };
}

/**
 * @param {number} preferredLeft
 * @param {number} targetWidth
 * @param {number} viewportWidth
 */
export function clampZoomImageLeft(preferredLeft, targetWidth, viewportWidth) {
  return Math.min(Math.max(preferredLeft, 0), Math.max(0, viewportWidth - targetWidth));
}
export const GRID_TILE_SHUFFLE_SEED = 20260708;
export const PORTFOLIO_PIN_THRESHOLD_PX = 0;
export const PORTFOLIO_ZOOM_HANDOFF_PROGRESS = 0.03;
export const CAPTION_ON_LIGHT_IMAGE_PHASE3_START = 0.22;

/** @type {{ first: { left: number; top: number; width: number; height: number } | null; second: { left: number; top: number; width: number; height: number } | null }} */
export const portfolioZoomHandoffRects = { first: null, second: null };

/**
 * @param {{ left: number; top: number; width: number; height: number } | null} first
 * @param {{ left: number; top: number; width: number; height: number } | null} second
 */
export function setPortfolioZoomHandoffRects(first, second) {
  if (first) {
    portfolioZoomHandoffRects.first = first;
  }

  if (second) {
    portfolioZoomHandoffRects.second = second;
  }
}

/**
 * @param {number} viewportWidth
 * @param {boolean} isMobile
 * @returns {{ width: number; left: number }}
 */
export function getGridSectionFrame(viewportWidth, isMobile) {
  const ratio = isMobile ? GRID_SECTION_WIDTH_RATIO_MOBILE : GRID_SECTION_WIDTH_RATIO;
  const width = viewportWidth * ratio;
  const left = (viewportWidth - width) / 2;

  return { width, left };
}

/** Grid slot for phase-2 zoom (~row 1, col 2 in the reference 10-col layout) */
export const THIRD_ZOOM_GRID_ROW = 1;
export const THIRD_ZOOM_GRID_COL = 2;
/** Grid slot for phase-3 zoom (~row 3, col 8 in the reference 10-col layout) */
export const SECOND_ZOOM_GRID_ROW = 3;
export const SECOND_ZOOM_GRID_COL = 8;
/** @deprecated use getSecondZoomTileIndex with live column count */
export const SECOND_ZOOM_TILE_INDEX = 38;
/** 1-based grid slot for phase-2 zoom on mobile (8th tile) */
export const THIRD_ZOOM_TILE_INDEX_MOBILE = 7;

/** Mobile grid layout (original — unchanged) */
export const GRID_IMAGES_PER_ROW_MOBILE = 5;
export const GRID_GAP_PX = 34;
export const GRID_HORIZONTAL_PADDING_PX_MOBILE = 0;
export const GRID_SECTION_WIDTH_RATIO_MOBILE = 0.95;
/** 1-based grid slot for phase-3 zoom (14th tile) */
export const SECOND_ZOOM_TILE_INDEX_MOBILE = 13;
export const GRID_ZOOM_TRACK_VH_MOBILE = 620;
export const GRID_ZOOM_TRACK_VH_DESKTOP = 500;
export const GRID_SECTION_BOTTOM_SPACER_VH_MOBILE = 24;
export const GRID_SECTION_BOTTOM_SPACER_VH_DESKTOP = 16;
/** Top padding before Our Spirits content — pairs with bottom spacer for grid→spirits gap */
export const SPIRITS_SECTION_TOP_GAP_VH_MOBILE = 3;
export const SPIRITS_SECTION_TOP_GAP_VH_DESKTOP = 4;

/** @deprecated use getGridZoomTrackVh */
export const GRID_ZOOM_TRACK_VH = GRID_ZOOM_TRACK_VH_MOBILE;
/** @deprecated use getGridSectionBottomSpacerVh */
export const GRID_SECTION_BOTTOM_SPACER_VH = GRID_SECTION_BOTTOM_SPACER_VH_MOBILE;

export function getGridZoomTrackVh(isMobile) {
  return isMobile ? GRID_ZOOM_TRACK_VH_MOBILE : GRID_ZOOM_TRACK_VH_DESKTOP;
}

export function getGridSectionBottomSpacerVh(isMobile) {
  return isMobile ? GRID_SECTION_BOTTOM_SPACER_VH_MOBILE : GRID_SECTION_BOTTOM_SPACER_VH_DESKTOP;
}

export function getSpiritsSectionTopGapVh(isMobile) {
  return isMobile ? SPIRITS_SECTION_TOP_GAP_VH_MOBILE : SPIRITS_SECTION_TOP_GAP_VH_DESKTOP;
}

/**
 * @param {number} gridWidth
 * @param {number} gap
 * @param {number} [maxTileWidth]
 */
export function getDesktopColumnCount(
  gridWidth,
  gap,
  maxTileWidth = GRID_DESKTOP_MAX_TILE_WIDTH_PX
) {
  let cols = GRID_DESKTOP_MIN_COLS;

  while (cols < GRID_DESKTOP_MAX_COLS) {
    const tileWidth = (gridWidth - (cols - 1) * gap) / cols;

    if (tileWidth <= maxTileWidth) {
      break;
    }

    cols += 1;
  }

  return cols;
}

/**
 * @param {number} cols
 * @param {number} _rowCount
 * @param {GridTile[]} tiles
 * @param {boolean} [isMobile]
 */
export function getThirdZoomTileIndex(cols, _rowCount, tiles, isMobile = false) {
  if (tiles.length === 0) {
    return 0;
  }

  if (isMobile) {
    return Math.min(THIRD_ZOOM_TILE_INDEX_MOBILE, tiles.length - 1);
  }

  const col = Math.min(THIRD_ZOOM_GRID_COL, cols - 1);
  const index = THIRD_ZOOM_GRID_ROW * cols + col;

  return Math.min(index, tiles.length - 1);
}

/**
 * @param {number} cols
 * @param {number} _rowCount
 * @param {GridTile[]} tiles
 * @param {boolean} [isMobile]
 */
export function getSecondZoomTileIndex(cols, _rowCount, tiles, isMobile = false) {
  if (tiles.length === 0) {
    return 0;
  }

  if (isMobile) {
    return Math.min(SECOND_ZOOM_TILE_INDEX_MOBILE, tiles.length - 1);
  }

  const col = Math.min(SECOND_ZOOM_GRID_COL, cols - 1);
  const index = SECOND_ZOOM_GRID_ROW * cols + col;

  return Math.min(index, tiles.length - 1);
}

export const SCROLL_PHASE_ZOOM1_END = 0.27;
export const SCROLL_PHASE_MIDDLE_ZOOM_END = 0.52;
export const SCROLL_PHASE_CAPTION_END = 0.60;
export const SCROLL_PHASE_ZOOM2_END = 0.75;
export const CAPTION_RETURN_FADE_MULTIPLIER = 2;

export const GRID_WORD_COLLISION_PADDING_PX = 10;
export const GRID_WORD_COLLISION_PADDING_PX_MD = 14;

/** Build text above first zoomed image — edit copy in portfolioGridData.js */
export const BUILD_TEXT = {
  de: 'Ein paar\nDinge von\ndem, was wir\nbisher taten',
  en: 'a few\nthings of\nwhat we did\nso far',
};

/** Text left of middle zoom image — edit copy in portfolioGridData.js */
export const MIDDLE_ZOOM_TEXT = {
  de: 'Design,\ndas\nwirkt',
  en: 'design\nthat\nlands',
};

/** Caption below first zoomed image — flies in left to right, right-aligned */
export const ZOOM_CAPTION = {
  de: 'Apps, die\nMenschen\nbegleiten',
  en: 'that\nsupport\npeople',
};

/** @type {{ de: string; en: string }} */
export const GRID_MANIFEST = {
  de: 'lets\nwork\ntogether',
  en: 'lets\nwork\ntogether',
};

export const GRID_MANIFEST_FONT_MIN = '3.575rem';
export const GRID_MANIFEST_FONT_PREFERRED = '13.41vw';
export const GRID_MANIFEST_FONT_MAX = '8.9375rem';

/** @type {GridTile[]} */
export const GRID_TILES = [
  { id: 'tile-01', thumbnail: imgAdobe, projectId: 'photo' },
  { id: 'tile-02', thumbnail: imgLmbServices, projectId: 'lmb' },
  { id: 'tile-03', thumbnail: imgLmbBrands, projectId: 'lmb' },
  { id: 'tile-04', thumbnail: imgLmbCopy, projectId: 'lmb' },
  { id: 'tile-05', thumbnail: imgBewusstseinstage, projectId: 'bewusstseinstage' },
  { id: 'tile-06', thumbnail: imgRemaxWheel, projectId: 'carsearch' },
  { id: 'tile-07', thumbnail: imgRemaxLogin, projectId: 'carsearch' },
  { id: 'tile-08', thumbnail: imgMylingra, projectId: 'mylingra' },
  { id: 'tile-09', thumbnail: imgRsqExpertise, projectId: 'rsq' },
  { id: 'tile-10', thumbnail: imgVoca, projectId: 'voca' },
  { id: 'tile-11', thumbnail: imgRsqHero, projectId: 'rsq-hero' },
];

/**
 * @param {number} sectionWidth
 * @param {number} sectionLeft
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @param {number} [imageAspectRatio]
 * @returns {{
 *   textColumnWidth: number;
 *   textColumnLeft: number;
 *   targetWidth: number;
 *   targetHeight: number;
 *   targetLeft: number;
 *   targetTop: number;
 * }}
 */
export function getDesktopZoomImageFrame(
  sectionWidth,
  sectionLeft,
  viewportWidth,
  viewportHeight,
  imageAspectRatio = DESKTOP_IMAGE_ASPECT_RATIO
) {
  const textColumnWidth = sectionWidth * DESKTOP_TEXT_COLUMN_RATIO;
  const imageColumnWidth = sectionWidth - textColumnWidth - DESKTOP_COLUMN_GAP_PX;
  const baseWidth = imageColumnWidth;
  const baseHeight = baseWidth / imageAspectRatio;
  const { targetWidth, targetHeight } = getPrimaryZoomImageDimensions(
    baseWidth,
    baseHeight,
    viewportWidth,
    viewportHeight
  );
  const columnLeft = sectionLeft + textColumnWidth + DESKTOP_COLUMN_GAP_PX;
  const targetLeft = clampZoomImageLeft(
    columnLeft + (baseWidth - targetWidth) / 2,
    targetWidth,
    viewportWidth
  );
  const targetTop = (viewportHeight - targetHeight) / 2;

  return {
    textColumnWidth,
    textColumnLeft: sectionLeft,
    targetWidth,
    targetHeight,
    targetLeft,
    targetTop,
  };
}

/**
 * @param {number} seed
 */
function createSeededRandom(seed) {
  let state = seed >>> 0;

  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

/**
 * @param {GridTile[]} tiles
 * @param {number} seed
 * @returns {GridTile[]}
 */
function seededShuffleTiles(tiles, seed) {
  const shuffled = [...tiles];
  const random = createSeededRandom(seed);

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = shuffled[index];
    shuffled[index] = shuffled[swapIndex];
    shuffled[swapIndex] = current;
  }

  return shuffled;
}

const FIRST_ZOOM_PROJECT_ID = 'rsq-hero';

/**
 * @param {GridTile[]} tiles
 * @returns {GridTile[]}
 */
function pinFirstZoomTileLast(tiles) {
  if (tiles.length === 0) {
    return tiles;
  }

  const lastIndex = tiles.length - 1;
  const pinnedIndex = tiles.findIndex((tile) => tile.projectId === FIRST_ZOOM_PROJECT_ID);
  const source = GRID_TILES.find((tile) => tile.projectId === FIRST_ZOOM_PROJECT_ID);

  if (pinnedIndex === -1) {
    if (!source) {
      return tiles;
    }

    tiles[lastIndex] = { ...source, id: tiles[lastIndex].id };
    return tiles;
  }

  if (pinnedIndex !== lastIndex) {
    const currentLast = tiles[lastIndex];
    tiles[lastIndex] = tiles[pinnedIndex];
    tiles[pinnedIndex] = currentLast;
  }

  return tiles;
}

/** Fisher-Yates shuffled pool — stable across renders (module init, one seed). */
const SHUFFLED_GRID_TILE_POOL = seededShuffleTiles(GRID_TILES, GRID_TILE_SHUFFLE_SEED);

/**
 * @param {number} targetCount
 * @returns {GridTile[]}
 */
export function buildViewportGridTiles(targetCount) {
  const filledTiles = Array.from({ length: targetCount }, (_, index) => {
    const source = SHUFFLED_GRID_TILE_POOL[index % SHUFFLED_GRID_TILE_POOL.length];

    return {
      ...source,
      id: `tile-${String(index + 1).padStart(2, '0')}`,
    };
  });

  return pinFirstZoomTileLast(seededShuffleTiles(filledTiles, GRID_TILE_SHUFFLE_SEED + targetCount));
}
